import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { loadSession, saveSession } from "./session";

/**
 * Window manager: owns every open window's geometry, stacking order and state.
 * Deliberately has no static import of the app registry — the registry is
 * injected as a prop so app components can call useWindows() without a cycle.
 */

const WindowContext = createContext(null);

export const useWindows = () => {
  const ctx = useContext(WindowContext);
  if (!ctx) throw new Error("useWindows must be used inside <WindowProvider>");
  return ctx;
};

export const MOBILE_QUERY = "(max-width: 900px), (pointer: coarse)";

export function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return mobile;
}

/** Geometry for a snap zone within a desktop surface of W x H. */
export function zoneRect(zone, W, H) {
  const halfW = Math.round(W / 2);
  const halfH = Math.round(H / 2);
  switch (zone) {
    case "left":
      return { x: 0, y: 0, w: halfW, h: H };
    case "right":
      return { x: W - halfW, y: 0, w: halfW, h: H };
    case "top-left":
      return { x: 0, y: 0, w: halfW, h: halfH };
    case "top-right":
      return { x: W - halfW, y: 0, w: halfW, h: halfH };
    case "bottom-left":
      return { x: 0, y: H - halfH, w: halfW, h: halfH };
    case "bottom-right":
      return { x: W - halfW, y: H - halfH, w: halfW, h: halfH };
    case "max":
    default:
      return { x: 0, y: 0, w: W, h: H };
  }
}

const MIN_W = 340;
const MIN_H = 240;

function initialRect(app, index, surface) {
  const w = Math.min(app.width || 900, Math.max(MIN_W, surface.w - 80));
  const h = Math.min(app.height || 600, Math.max(MIN_H, surface.h - 60));
  const step = 28 * (index % 6);
  const x = Math.max(0, Math.round((surface.w - w) / 2) - 60 + step);
  const y = Math.max(0, Math.round((surface.h - h) / 2) - 40 + step);
  return { x, y, w, h };
}

function reducer(state, action) {
  switch (action.type) {
    case "OPEN": {
      const { app, surface, payload } = action;
      const existing = state.windows.find((w) => w.appId === app.id);
      if (existing) {
        // Opening a document in an app already running hands it the new file.
        const focused = reducer(state, {
          type: "FOCUS",
          id: existing.id,
          restore: true,
        });
        if (!payload) return focused;
        return {
          ...focused,
          windows: focused.windows.map((w) =>
            w.id === existing.id ? { ...w, payload, payloadKey: state.zTop + 1 } : w
          ),
        };
      }
      const z = state.zTop + 1;
      const win = {
        id: `${app.id}-${z}`,
        appId: app.id,
        payload: payload || null,
        payloadKey: z,
        z,
        minimized: false,
        maximized: Boolean(app.startMaximized),
        snap: null,
        restoreRect: null,
        ...initialRect(app, state.opened, surface),
      };
      return {
        ...state,
        windows: [...state.windows, win],
        zTop: z,
        opened: state.opened + 1,
        activeId: win.id,
      };
    }

    case "CLOSE": {
      const windows = state.windows.filter((w) => w.id !== action.id);
      const top = [...windows]
        .filter((w) => !w.minimized)
        .sort((a, b) => b.z - a.z)[0];
      return { ...state, windows, activeId: top ? top.id : null };
    }

    case "FOCUS": {
      const win = state.windows.find((w) => w.id === action.id);
      if (!win) return state;
      if (state.activeId === action.id && !win.minimized) return state;
      const z = state.zTop + 1;
      return {
        ...state,
        zTop: z,
        activeId: action.id,
        windows: state.windows.map((w) =>
          w.id === action.id
            ? { ...w, z, minimized: action.restore ? false : w.minimized }
            : w
        ),
      };
    }

    case "MINIMIZE":
      return {
        ...state,
        activeId: state.activeId === action.id ? null : state.activeId,
        windows: state.windows.map((w) =>
          w.id === action.id ? { ...w, minimized: true } : w
        ),
      };

    /** Taskbar behaviour: focused window minimizes, anything else raises. */
    case "TOGGLE": {
      const win = state.windows.find((w) => w.appId === action.appId);
      if (!win) return state;
      if (!win.minimized && state.activeId === win.id) {
        return reducer(state, { type: "MINIMIZE", id: win.id });
      }
      return reducer(state, { type: "FOCUS", id: win.id, restore: true });
    }

    case "SET_RECT":
      return {
        ...state,
        windows: state.windows.map((w) =>
          w.id === action.id
            ? { ...w, ...action.rect, maximized: false, snap: null }
            : w
        ),
      };

    case "TOGGLE_MAX":
      return {
        ...state,
        windows: state.windows.map((w) => {
          if (w.id !== action.id) return w;
          if (w.maximized || w.snap) {
            const r = w.restoreRect || { x: w.x, y: w.y, w: w.w, h: w.h };
            return { ...w, ...r, maximized: false, snap: null, restoreRect: null };
          }
          return {
            ...w,
            maximized: true,
            snap: null,
            restoreRect: { x: w.x, y: w.y, w: w.w, h: w.h },
          };
        }),
      };

    case "SNAP": {
      const { id, zone, surface } = action;
      return {
        ...state,
        windows: state.windows.map((w) => {
          if (w.id !== id) return w;
          const restoreRect =
            w.restoreRect || (w.maximized ? null : { x: w.x, y: w.y, w: w.w, h: w.h });
          if (zone === "max") {
            return { ...w, maximized: true, snap: null, restoreRect };
          }
          return {
            ...w,
            ...zoneRect(zone, surface.w, surface.h),
            maximized: false,
            snap: zone,
            restoreRect,
          };
        }),
      };
    }

    /** Clamp every floating window back inside the surface after a resize. */
    case "REFLOW":
      return {
        ...state,
        windows: state.windows.map((w) => {
          if (w.maximized) return w;
          if (w.snap) return { ...w, ...zoneRect(w.snap, action.surface.w, action.surface.h) };
          const width = Math.min(w.w, action.surface.w);
          const height = Math.min(w.h, action.surface.h);
          return {
            ...w,
            w: width,
            h: height,
            x: Math.max(0, Math.min(w.x, action.surface.w - width)),
            y: Math.max(0, Math.min(w.y, action.surface.h - height)),
          };
        }),
      };

    case "SHOW_DESKTOP": {
      const anyVisible = state.windows.some((w) => !w.minimized);
      return {
        ...state,
        activeId: anyVisible ? null : state.activeId,
        windows: state.windows.map((w) => ({ ...w, minimized: anyVisible })),
      };
    }

    case "CLOSE_ALL":
      return { ...state, windows: [], activeId: null };

    default:
      return state;
  }
}

const INITIAL = {
  windows: [],
  zTop: 10,
  activeId: null,
  opened: 0,
  hydrated: false,
};

/** Rehydrates the desktop from the tab's session, if there is one. */
function init(apps) {
  const saved = loadSession(new Set(apps.map((a) => a.id)));
  return saved ? { ...INITIAL, ...saved, hydrated: true } : INITIAL;
}

export function WindowProvider({ apps, surface, children }) {
  const [state, dispatch] = useReducer(reducer, apps, init);

  useEffect(() => {
    saveSession(state);
  }, [state.windows, state.activeId]); // eslint-disable-line

  const appMap = useMemo(
    () => Object.fromEntries(apps.map((a) => [a.id, a])),
    [apps]
  );

  const { w: surfaceW, h: surfaceH } = surface;
  useEffect(() => {
    if (surfaceW > 0) {
      dispatch({ type: "REFLOW", surface: { w: surfaceW, h: surfaceH } });
    }
  }, [surfaceW, surfaceH]);

  const open = useCallback(
    (appId, payload) => {
      const app = appMap[appId];
      if (!app) return;
      if (app.external) {
        window.open(app.external, "_blank", "noopener,noreferrer");
        return;
      }
      dispatch({ type: "OPEN", app, surface, payload });
    },
    [appMap, surface]
  );

  const value = useMemo(
    () => ({
      apps,
      appMap,
      surface,
      windows: state.windows,
      activeId: state.activeId,
      hydrated: state.hydrated,
      open,
      close: (id) => dispatch({ type: "CLOSE", id }),
      focus: (id) => dispatch({ type: "FOCUS", id, restore: true }),
      minimize: (id) => dispatch({ type: "MINIMIZE", id }),
      toggleApp: (appId) => dispatch({ type: "TOGGLE", appId }),
      toggleMax: (id) => dispatch({ type: "TOGGLE_MAX", id }),
      setRect: (id, rect) => dispatch({ type: "SET_RECT", id, rect }),
      snap: (id, zone) => dispatch({ type: "SNAP", id, zone, surface }),
      showDesktop: () => dispatch({ type: "SHOW_DESKTOP" }),
      closeAll: () => dispatch({ type: "CLOSE_ALL" }),
    }),
    [apps, appMap, surface, state.windows, state.activeId, state.hydrated, open]
  );

  return (
    <WindowContext.Provider value={value}>{children}</WindowContext.Provider>
  );
}

export { MIN_W, MIN_H };
